import {
  SlideObject,
  SlideObjectStyle,
  TableData,
  createObjectId,
  defaultLocks,
  resolvedLineHeight,
} from '../types';
import { sanitizePresentationHtml } from './textSanitize';

export const TABLE_MAX_COLS = 12;
export const TABLE_MAX_ROWS = 40;
export const TABLE_DEFAULT_COLS = 3;
export const TABLE_DEFAULT_ROWS = 4;

export const TABLE_HEADER_BG = '#1B3967';
export const TABLE_HEADER_COLOR = '#FFFFFF';
export const TABLE_BODY_COLOR = '#111111';
export const TABLE_GRID = '#998A6B';
export const TABLE_ZEBRA = '#F5F0E8';
export const TABLE_BODY_BG = '#FFFFFF';
export const TABLE_FONT_SIZE = 18;
export const TABLE_CELL_PADDING = 8;
export const TABLE_STROKE_WIDTH = 1;
export const TABLE_MIN_FONT_SIZE = 10;
export const TABLE_MIN_STROKE_WIDTH = 0.5;
export const TABLE_MIN_FRACTION = 0.04;
export const TABLE_PAGE_MARGIN = 24;

function emptyCell(): { id: string; content: string } {
  return { id: createObjectId('cell'), content: '' };
}

export function equalFractions(count: number): number[] {
  const n = Math.max(1, count);
  return Array.from({ length: n }, () => 1 / n);
}

export function normalizeFractions(values: number[] | undefined, count: number): number[] {
  const n = Math.max(1, count);
  const fallback = 1 / n;
  const raw = Array.from({ length: n }, (_, index) => {
    const value = Number(values?.[index]);
    return Number.isFinite(value) && value > 0 ? value : fallback;
  });
  const sum = raw.reduce((total, value) => total + value, 0) || 1;
  return raw.map((value) => value / sum);
}

export function resizeFractions(current: number[], nextCount: number): number[] {
  const n = Math.max(1, nextCount);
  if (current.length === n) return normalizeFractions(current, n);
  if (n < current.length) return normalizeFractions(current.slice(0, n), n);
  const avg = current.length
    ? current.reduce((total, value) => total + value, 0) / current.length
    : 1 / n;
  return normalizeFractions(
    [...current, ...Array.from({ length: n - current.length }, () => avg)],
    n,
  );
}

export function resizeFractionPair(
  values: number[],
  index: number,
  delta: number,
  minFraction = TABLE_MIN_FRACTION,
): number[] {
  if (index < 0 || index >= values.length - 1) return values;
  const next = [...values];
  const pair = next[index] + next[index + 1];
  const minShare = Math.min(minFraction, pair / 2);
  const left = Math.max(minShare, Math.min(pair - minShare, next[index] + delta));
  next[index] = left;
  next[index + 1] = pair - left;
  return next;
}

function clampGrid(cols: number, rows: number): { cols: number; rows: number } {
  return {
    cols: Math.max(1, Math.min(TABLE_MAX_COLS, Math.floor(cols) || 1)),
    rows: Math.max(1, Math.min(TABLE_MAX_ROWS, Math.floor(rows) || 1)),
  };
}

export function cellPlainText(html?: string): string {
  return String(html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

export function wrapCellText(text: string): string {
  const clean = sanitizePresentationHtml(String(text || '').replace(/\n/g, '<br>'));
  if (!clean || clean === '<p></p>') return '';
  if (/^<(p|br|ul|ol|span)\b/i.test(clean)) return clean;
  return `<p>${clean}</p>`;
}

export function tableChrome(style?: SlideObjectStyle | null): { padding: number; strokeWidth: number } {
  const padding = Number(style?.padding);
  const strokeWidth = Number(style?.strokeWidth);
  return {
    padding: Number.isFinite(padding) && padding >= 0 ? padding : TABLE_CELL_PADDING,
    strokeWidth: Number.isFinite(strokeWidth) && strokeWidth > 0 ? strokeWidth : TABLE_STROKE_WIDTH,
  };
}

export function renderedStrokeWidth(style?: SlideObjectStyle | null): number {
  return Math.max(TABLE_MIN_STROKE_WIDTH, tableChrome(style).strokeWidth);
}

export function minTableCellHeight(style?: SlideObjectStyle | null): number {
  const fontSize = Number(style?.fontSize) || TABLE_FONT_SIZE;
  const chrome = tableChrome(style);
  return fontSize * resolvedLineHeight(style || undefined) + chrome.padding * 2;
}

export function normalizeTable(table?: TableData | null): TableData {
  const source = table && Array.isArray(table.rows) && table.rows.length
    ? table
    : { headerRow: true, colWidths: [1], rowHeights: [1], rows: [{ cells: [emptyCell()] }] };
  const rawCols = Math.max(
    1,
    ...source.rows.map((row) => row?.cells?.length || 0),
    source.colWidths?.length || 0,
  );
  const { cols, rows } = clampGrid(rawCols, source.rows.length);
  return {
    headerRow: source.headerRow !== false,
    colWidths: normalizeFractions(source.colWidths, cols),
    rowHeights: normalizeFractions(source.rowHeights, rows),
    rows: Array.from({ length: rows }, (_, rowIndex) => ({
      cells: Array.from({ length: cols }, (__, colIndex) => {
        const cell = source.rows[rowIndex]?.cells?.[colIndex];
        return {
          id: cell?.id || createObjectId('cell'),
          content: sanitizePresentationHtml(cell?.content),
        };
      }),
    })),
  };
}

export function createDefaultTable(cols = TABLE_DEFAULT_COLS, rows = TABLE_DEFAULT_ROWS): TableData {
  const size = clampGrid(cols, rows);
  return {
    headerRow: true,
    colWidths: equalFractions(size.cols),
    rowHeights: equalFractions(size.rows),
    rows: Array.from({ length: size.rows }, () => ({
      cells: Array.from({ length: size.cols }, () => emptyCell()),
    })),
  };
}

export function tableBrandStyle(overrides?: {
  fontSize?: number;
  headerBg?: string;
  grid?: string;
}): SlideObjectStyle {
  return {
    fontFamily: 'Akkurat Std',
    fontSize: overrides?.fontSize ?? TABLE_FONT_SIZE,
    fontWeight: 400,
    color: TABLE_HEADER_COLOR,
    backgroundColor: overrides?.headerBg ?? TABLE_HEADER_BG,
    strokeColor: overrides?.grid ?? TABLE_GRID,
    strokeWidth: TABLE_STROKE_WIDTH,
    padding: TABLE_CELL_PADDING,
    textAlign: 'left',
    verticalAlign: 'middle',
  };
}

export function createDefaultTableObject(input: {
  z: number;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  cols?: number;
  rows?: number;
  fontSize?: number;
  headerBg?: string;
  grid?: string;
}): SlideObject {
  return {
    id: createObjectId('table'),
    type: 'table',
    x: input.x ?? 160,
    y: input.y ?? 200,
    w: input.w ?? 960,
    h: input.h ?? 360,
    z: input.z,
    locks: defaultLocks(),
    table: createDefaultTable(input.cols, input.rows),
    style: tableBrandStyle({
      fontSize: input.fontSize,
      headerBg: input.headerBg,
      grid: input.grid,
    }),
  };
}

export function resizeTableGrid(table: TableData | undefined, cols: number, rows: number): TableData {
  const current = normalizeTable(table);
  const size = clampGrid(cols, rows);
  return {
    headerRow: current.headerRow,
    colWidths: resizeFractions(current.colWidths, size.cols),
    rowHeights: resizeFractions(current.rowHeights || equalFractions(current.rows.length), size.rows),
    rows: Array.from({ length: size.rows }, (_, rowIndex) => ({
      cells: Array.from({ length: size.cols }, (__, colIndex) => (
        current.rows[rowIndex]?.cells[colIndex] || emptyCell()
      )),
    })),
  };
}

export function resizeTableColumns(
  table: TableData | undefined,
  dividerIndex: number,
  deltaFraction: number,
): TableData {
  const current = normalizeTable(table);
  return {
    ...current,
    colWidths: resizeFractionPair(current.colWidths, dividerIndex, deltaFraction),
  };
}

export function resizeTableRows(
  table: TableData | undefined,
  dividerIndex: number,
  deltaFraction: number,
): TableData {
  const current = normalizeTable(table);
  return {
    ...current,
    rowHeights: resizeFractionPair(current.rowHeights || equalFractions(current.rows.length), dividerIndex, deltaFraction),
  };
}

export function setTableHeaderRow(table: TableData | undefined, headerRow: boolean): TableData {
  return { ...normalizeTable(table), headerRow };
}

export function tableFromGrid(grid: string[][], headerRow = true): TableData {
  const rows = Math.max(1, grid.length);
  const cols = Math.max(1, ...grid.map((row) => row.length));
  const size = clampGrid(cols, rows);
  return {
    headerRow,
    colWidths: equalFractions(size.cols),
    rowHeights: equalFractions(size.rows),
    rows: Array.from({ length: size.rows }, (_, rowIndex) => ({
      cells: Array.from({ length: size.cols }, (__, colIndex) => ({
        id: createObjectId('cell'),
        content: wrapCellText(grid[rowIndex]?.[colIndex] || ''),
      })),
    })),
  };
}

export function applyGridToTable(table: TableData | undefined, grid: string[][]): TableData {
  const current = normalizeTable(table);
  const next = tableFromGrid(grid, current.headerRow);
  return {
    ...next,
    colWidths: resizeFractions(current.colWidths, next.colWidths.length),
    rowHeights: resizeFractions(current.rowHeights || equalFractions(current.rows.length), next.rows.length),
  };
}

export function cloneTableData(table: TableData | undefined): TableData {
  const current = normalizeTable(table);
  return {
    headerRow: current.headerRow,
    colWidths: [...current.colWidths],
    rowHeights: [...(current.rowHeights || equalFractions(current.rows.length))],
    rows: current.rows.map((row) => ({
      cells: row.cells.map((cell) => ({ id: createObjectId('cell'), content: cell.content })),
    })),
  };
}

export function fitTableObjectToPage(
  object: SlideObject,
  pageSize: { width: number; height: number },
): SlideObject {
  if (object.type !== 'table') return object;
  const table = normalizeTable(object.table);
  const maxH = Math.max(40, pageSize.height - object.y - TABLE_PAGE_MARGIN);
  const style = { ...(object.style || {}) };
  let fontSize = Number(style.fontSize) || TABLE_FONT_SIZE;
  const heightFor = (size: number) => table.rows.length * minTableCellHeight({ ...style, fontSize: size });
  while (fontSize > TABLE_MIN_FONT_SIZE && heightFor(fontSize) > maxH) {
    fontSize -= 1;
  }
  return {
    ...object,
    table,
    h: Math.min(maxH, Math.max(object.h, heightFor(fontSize), 40)),
    style: {
      ...style,
      fontSize,
      padding: tableChrome({ ...style, fontSize }).padding,
    },
  };
}

export function tableSearchText(object: Pick<SlideObject, 'type' | 'table'>): string {
  if (object.type !== 'table') return '';
  return normalizeTable(object.table).rows
    .flatMap((row) => row.cells.map((cell) => cellPlainText(cell.content)))
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}
